import { DatePicker } from "antd";
import { cloneDeep, set } from "lodash";
import moment from "moment";
import "./style.scss";
import React, { FC, HTMLAttributes, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getAuditLogDetails, getSubFilterDetails } from "../../../redux/slices";
import CustomDropdown from "../../../components/reusable-ui/custom-dropdown";
import {
  CTA_SIZE,
  CTA_TYPE,
  DATE_TIME_FORMATS,
  SALES_AUDIT_OPTIONS_MAP,
} from "../../../data/constants";
import { RootState } from "../../../redux/store";
import { convertArrayToObject } from "../../../helpers/catalog-helper";
import { isSourcePos } from "../../../helpers/common-helper";
import { isMobileTabDevice } from "../../../helpers/device-info-helper";
import BranchFilterComponent from "../../../components/reusable-ui/branch-filter";
import CtaComponent from "../../../components/reusable-ui/cta";
import SearchInputComponent from "../../../components/reusable-ui/search-input";
import { allowEverything } from "../../../utils/regex";
import AuditDetails from "./audit-details";

interface SearchFilterType {
  pageSize: number;
  branchId?: any;
  date?: string;
  lastKey?: any;
}

const SalesAuditLog: FC<HTMLAttributes<HTMLDivElement>> = () => {
  const [searchIcon, setSearchIcon] = useState<boolean>(false);
  const userData = useSelector((state: any) => state.businessReducer.userData);
  const allowedBusinessBranchesDataMap = convertArrayToObject(
    userData.permittedBranches,
    "branchId"
  );
  //to track if the payload values has been updated
  const [isBranchUpdated, setIsBranchUpdated] = useState<boolean>(false);
  const dispatch = useDispatch();
  const [showFilterResults, setShowFilterResults] = useState<Boolean>(false);
  const [ctaDisabled, setCtaDisabled] = useState(true);
  const [searchData, setSearchData] = useState<any>({
    value: "",
    results: [],
  });
  const [selectedId, setSelectedId] = useState<string>(
    SALES_AUDIT_OPTIONS_MAP.allorders.id
  );
  const [selectedSubType, setSelectedSubType] = useState<string>("");
  const [invoiceSummary, setInvoiceSummary] = useState<boolean>(false);
  const currentBranchId = useSelector(
    (state: any) => state.businessReducer?.businessData?.branchId
  );
  const currentBusinessDate = useSelector(
    (state: any) => state.businessReducer?.businessData?.activationDate
  );
  const { permittedBranchesData } = useSelector((state: RootState) => ({
    permittedBranchesData: state.businessReducer.userData.permittedBranches,
  }));
  const { allBrandsData } = useSelector((state: RootState) => ({
    allBrandsData: convertArrayToObject(
      state.businessReducer.allBrandsData,
      "brandName"
    ),
  }));
  const auditLogData = useSelector(
    (state: RootState) => state.accountingReducer?.auditDetails
  );

  let intialSearchState: SearchFilterType = {
    pageSize: 10,
    branchId: getDefaultBranchId(),
    date: moment(new Date()).format(DATE_TIME_FORMATS.yearMonthDay),
    lastKey: {},
  };

  const [state, setState] = useState({
    settlementId: "",
    viewSettlement: false,
    searchFilters: intialSearchState,
    devicePagination: {},
  });
  const [filterConfig, setFilterConfig] = useState<any>({
    periods: [],
    branches: getDefaultBranchId(),
    bizzBrandNames: Object.keys(allBrandsData),
  });

  function getPayload() {
    return {
      branchIds: filterConfig.branches
        ? filterConfig.branches
        : state.searchFilters.branchId,
      periods: [
        moment(state.searchFilters.date).format(
          DATE_TIME_FORMATS.yearMonthDay
        ) || moment(new Date()).format(DATE_TIME_FORMATS.yearMonthDay),
      ],
      bizzBrandNames: filterConfig.bizzBrandNames,
      pageSize: 100,
      isNew: true,
    };
  }

  const DROPDOWN_CONFIG = {
    brand_name: {
      onSubmit: (data: any) => handleFilterChange(data, "bizzBrandNames"),
      optionsMap: allBrandsData,
      placeholder: "Select Brand",
      preSelectedValues: filterConfig.bizzBrandNames,
      uniqueKey: "brandName",
      parentClass: "hover:!border-red hover:border",
      getListItemText: (data: any) => data?.brandName || "",
      isMultiSelect: true,
      dropdownHeading: "Brand Name",
    },
  };

  useEffect(() => {
    filterAudit();
  }, []);

  const onSearchInput = (searchStr: string) => {
    const results: any = [];
    if (searchStr && auditLogData?.results) {
      auditLogData?.results?.forEach(function (auditData: any) {
        if (
          auditData?.billNumber
            .toLocaleLowerCase()
            .includes(searchStr.toLocaleLowerCase())
        ) {
          results.push(auditData);
        }
      });
    }
    setSearchData({ value: searchStr, results });
  };

  const handleFilterChange = (value: any, fieldToUpdate: string) => {
    let dataToUpdate = cloneDeep(filterConfig);
    set(dataToUpdate, fieldToUpdate, value);
    setFilterConfig(dataToUpdate);
    //if any of payload value has been changed make apply cta clickable
    if (isBranchUpdated) {
      setCtaDisabled(false);
    }
    if (Object.keys(dataToUpdate).indexOf(fieldToUpdate) > -1) {
      setIsBranchUpdated(true);
    }
  };

  const handleonSelectedIdChange = (selectedItem: any) => {
    setSelectedId(selectedItem);
    setSelectedSubType("");
  };

  function filterAudit() {
    const dataToPost: any = getPayload();
    setShowFilterResults(true);
    if (selectedId && selectedId !== SALES_AUDIT_OPTIONS_MAP.allorders.id) {
      dataToPost["auditType"] = SALES_AUDIT_OPTIONS_MAP[selectedId].label;
      if (selectedSubType) {
        dataToPost["auditSubType"] = selectedSubType;
      }
    }
    if (selectedId === SALES_AUDIT_OPTIONS_MAP.allorders.id) {
      delete dataToPost?.auditType;
      delete dataToPost?.auditSubType;
    }
    dispatch(getAuditLogDetails({ dataToPost }));
    dispatch(getSubFilterDetails({ dataToPost }));
    setCtaDisabled(true);
    setInvoiceSummary(false);
  }

  function disabledDate(current: any) {
    const disableBeforeDate = moment(currentBusinessDate).startOf("day");
    const currentDate = moment().endOf("day");
    return current && (current < disableBeforeDate || current > currentDate);
  }

  function getDefaultBranchId() {
    if (isSourcePos()) return [currentBranchId];
    return allowedBusinessBranchesDataMap &&
      Object.keys(allowedBusinessBranchesDataMap)?.length > 0
      ? Object.keys(allowedBusinessBranchesDataMap)
      : "";
  }

  const onDateRangeSubmission = (data: any) => {
    handleFilter(
      data ? moment(data).format(DATE_TIME_FORMATS.yearMonthDay) : "",
      "date"
    );
    handleFilterChange(data, "periods");
  };

  function renderCustomDropDown(dropdownConfig: any) {
    return (
      <CustomDropdown
        {...dropdownConfig}
        dropdownSelectClass={"h-40 p-0 !rounded-[8px]"}
        parentClass={
          "!h-40 ml-12 smd:ml-[0%] rounded-[8px] items-center w-[160px] smd:w-[98%] sm:!mt-0 flex cursor-pointer"
        }
        labelClassName={"flex items-center"}
      />
    );
  }

  function handleFilter(value: string, name: string) {
    let filter = {} as SearchFilterType;
    filter = { ...state.searchFilters, [name]: value };
    setState({ ...state, searchFilters: filter });
  }

  function renderFilterMobileHeader() {
    return (
      <div className="flex justify-between w-[100%] smd:overflow-hidden border-b-[1px] p-16 bg-white">
        <div className="flex-col">
          <p className="font-semibold">
            {state.searchFilters.date
              ? moment(state.searchFilters.date).format(
                  DATE_TIME_FORMATS.yearMonthDay
                )
              : moment(new Date()).format(DATE_TIME_FORMATS.yearMonthDay)}
          </p>
          <p className="opacity-50">
            <span className="mr-2">
              {filterConfig.branches
                ? filterConfig.branches.length
                : permittedBranchesData.length}
            </span>
            <span className="pr-6 border-r-2">Branches</span>
            <span className="mr-2 ml-3">
              {filterConfig.bizzBrandNames?.length}
            </span>
            <span className="pr-6">Brands</span>
          </p>
        </div>
        <div className="flex justify-end float-right flex-row-reverse">
          <p
            className="text-[#397EF5] text-[14px] font-semibold"
            onClick={() => {
              setShowFilterResults(false);
            }}
          >
            Modify
          </p>
        </div>
      </div>
    );
  }

  function renderSubFilter() {
    return (
      <>
        {" "}
        <DatePicker
          className="custom-date-picker h-40 w-[180px] smd:!ml-0 smd:!mt-0 smd:items-center tb:w-[48%] sm:w-[98%] rounded-[8px] border-[#C4C4C4]"
          format={DATE_TIME_FORMATS.dayMonthYear}
          value={
            state.searchFilters.date
              ? moment(state.searchFilters.date)
              : undefined
          }
          disabledDate={disabledDate}
          onChange={onDateRangeSubmission}
        />
        <BranchFilterComponent
          branchDataList={permittedBranchesData}
          onApplyCtaClick={(branches: any) =>
            handleFilterChange(branches, "branches")
          }
          selectAllBranches={true}
          parentClass={`custom-branch-filter ml-12 !h-[40px] w-[180px] !rounded-[8px] tb:w-[48%] sm:!w-[98%] smd:ml-0 sm:!mt-0
             w-[220px]
          `}
          customClass={
            "!h-[40px] smd:text-[14px] !rounded-[8px] !border-[#bdbdbd]"
          }
        ></BranchFilterComponent>
        {renderCustomDropDown(DROPDOWN_CONFIG.brand_name)}
        <CtaComponent
          ctaSize={CTA_SIZE.small}
          ctaType={CTA_TYPE.active_cta}
          onCtaClick={filterAudit}
          customClass={`!h-40 ml-12 pb-10 smd:w-[98%] smd:ml-0 cursor-pointer`}
          ctaText={"Apply"}
          isDisabled={ctaDisabled}
        />
        {!isMobileTabDevice() && !searchIcon && (
          <div className="border-l-[1px] ml-12">
            <div
              className="w-[42px] mx-12 text-[13px] h-[40px] border border-[#3a5e8b1a] rounded-[6px] flex gap-1.25 justify-center items-center overflow-hidden cursor-pointer hover:border-[#9FCAFD] hover:border"
              onClick={() => setSearchIcon(true)}
            >
              <span className="w-[18px] rista-sprite ic-search-icon ml-[1%]"></span>
            </div>
          </div>
        )}
        {isMobileTabDevice() && (
          <hr className="w-[100%] h-1 mx-auto my-1 bg-gray-200 border-0 rounded smd:my-4" />
        )}
      </>
    );
  }

  function renderFilters() {
    return (
      <div className="flex smd:flex-col filter-container">
        {showFilterResults &&
        !((invoiceSummary && isMobileTabDevice()) || !isMobileTabDevice()) ? (
          renderFilterMobileHeader()
        ) : (
          <div className="flex w-full justify-around bg-white">
            <div className=" smd:mt-12 flex items-center gap-y-6 sm:gap-y-12 flex-wrap smd:px-16 tb:gap-x-12 py-12 tb:gap-y-16 px-16  w-full tb:pt-12 smd:pb-0 bg-white">
              {!searchIcon ? (
                <>
                  {((!invoiceSummary &&
                    !showFilterResults &&
                    isMobileTabDevice()) ||
                    !isMobileTabDevice()) &&
                    renderSubFilter()}
                </>
              ) : (
                <div className="flex w-[41%] pr-[1%] border-r-[1px]">
                  <SearchInputComponent
                    placeholder={"Search invoice number; ex - 1234"}
                    customClass={
                      "text-15 bg-[#fff] border-none focus:outline-none px-4  "
                    }
                    onSerachInput={onSearchInput}
                    searchMinLength={3}
                    autoFocus={true}
                    regexCheck={allowEverything}
                    elementWidthClass={
                      "w-[100%] bg-[#fff] border-none focus:outline-none "
                    }
                  />
                  <span
                    className="rista-sprite ic-cross-black"
                    onClick={() => {
                      setSearchIcon(false);
                    }}
                  ></span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
  function noFiltersApplied() {
    let { searchFilters } = state;
    return searchFilters.date === "" && searchFilters.branchId === "";
  }
  function renderAuditLogs() {
    return (
      <div className="sales-audit-container">
        {!noFiltersApplied() && (
          <>
            {renderFilters()}
            {!(
              (invoiceSummary && isMobileTabDevice()) ||
              !isMobileTabDevice()
            ) && (
              <SearchInputComponent
                placeholder={"Search invoice number; ex - 1234"}
                customClass={
                  "text-15 bg-[#fff] border-none focus:outline-none !py-4 px-[24px]"
                }
                onSerachInput={onSearchInput}
                searchMinLength={3}
                regexCheck={allowEverything}
                autoFocus={true}
                elementWidthClass={
                  "w-[100%] bg-[#fff] border-none focus:outline-none !py-12"
                }
              />
            )}
            <AuditDetails
              getPayload={getPayload}
              searchData={searchData}
              handleonSelectedIdChange={handleonSelectedIdChange}
              setSelectedSubType={setSelectedSubType}
              selectedId={selectedId}
              selectedSubType={selectedSubType}
              searchIcon={searchIcon}
              handleInvoiceSummary={setInvoiceSummary}
              invoiceSummary={invoiceSummary}
            />
          </>
        )}
      </div>
    );
  }
  return renderAuditLogs();
};
export default SalesAuditLog;
